import { z } from 'zod'

export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  service: z.string(),
  version: z.string(),
  uptimeSeconds: z.number().nonnegative(),
  /**
   * The automatic-send tick's pulse (CLI-741). `lastTickAt` is when the QStash cron last reached
   * this service; `stale` is true once that is older than three ticks — the signal an uptime
   * check matches on, because a log-based alert cannot notice something that stopped happening.
   * Null and not stale where no schedule exists (previews, local).
   */
  autoSend: z.object({
    lastTickAt: z.iso.datetime().nullable(),
    stale: z.boolean(),
    /** The kill switch, so an operator can see from outside that sends are held. */
    paused: z.object({ generation: z.boolean(), delivery: z.boolean() }),
  }),
})

export type HealthResponse = z.infer<typeof healthResponseSchema>

const probeSchema = z.object({
  ok: z.boolean(),
  /** How long the probe took, whichever way it went. */
  durationMs: z.number().nonnegative(),
  /** The failure, in words — a message, never a connection string or a token. */
  error: z.string().optional(),
})

/**
 * `GET /api/internal/health` — what `/api/health` deliberately leaves out (CLI-625).
 *
 * The open health route answers 200 as long as the process is up, because Cloud Run's probes must
 * not take a service out of rotation over a dependency it could ride out. This one goes and asks:
 * one round trip to the app schema, one to the warehouse (and whether it is a replica), one to the
 * data API's own health route — and reports the configuration the send path was built from (mail
 * mode, ledger, auth, scheduler) without exercising any of it. Behind the service token, because
 * it names infrastructure and each call costs a dependency a request. The post-deploy smoke test
 * (apps/api/scripts/smoke.ts) is its consumer.
 */
export const dependencyHealthResponseSchema = z.object({
  /** `ok` when every CONFIGURED dependency answered; an unconfigured one is a choice, not a fault. */
  status: z.enum(['ok', 'degraded']),
  service: z.string(),
  version: z.string(),
  checks: z.object({
    appDatabase: probeSchema.extend({ schema: z.string() }),
    warehouse: probeSchema.extend({
      configured: z.boolean(),
      /** `pg_is_in_recovery()` — true on the read replica, false on a primary, null if unreached. */
      replica: z.boolean().nullable(),
    }),
    dataApi: probeSchema.extend({
      configured: z.boolean(),
      /** The HTTP status its health route answered; null if the request never completed. */
      status: z.number().nullable(),
    }),
  }),
  config: z.object({
    /** WorkOS is fully configured, so sign-in works at all. */
    auth: z.boolean(),
    /** QStash is fully configured: scheduled sends, grant expiry, the tick. */
    scheduler: z.boolean(),
    mail: z.object({
      /** EMAIL_SENDING as deployed. */
      mode: z.enum(['off', 'live']),
      /** A SendGrid key is present — a fact, never a value. */
      provider: z.boolean(),
      /** Both of the above: a real mailer exists and a send would leave the building. */
      active: z.boolean(),
    }),
    deliveryLedger: z.enum(['off', 'on']),
  }),
})

export type DependencyHealthResponse = z.infer<typeof dependencyHealthResponseSchema>

/** The access probe's steps, in the order it runs them (apps/api/src/lib/access-probe.ts). */
export const ACCESS_PROBE_STEPS = [
  'assignments',
  'check',
  'effective',
  'grants',
  'pending',
] as const

/**
 * `POST /api/internal/access-probe` — the FGA read path against the canary account, behind the
 * service token. Cloud Scheduler calls it every five minutes; the post-deploy smoke test calls it
 * once. 200 is a pass, 503 a failure naming its step; unconfigured is a 503 with `code:
 * not_configured` and the ordinary error envelope, never a pass.
 */
export const accessProbeResponseSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    steps: z.record(z.enum(ACCESS_PROBE_STEPS), z.number()),
    durationMs: z.number(),
    stalePendingGrants: z.number(),
  }),
  z.object({
    ok: z.literal(false),
    step: z.enum(ACCESS_PROBE_STEPS),
    cause: z.string(),
    steps: z.partialRecord(z.enum(ACCESS_PROBE_STEPS), z.number()),
    durationMs: z.number(),
  }),
])

export type AccessProbeResponse = z.infer<typeof accessProbeResponseSchema>

/**
 * `GET /api/internal/provisioning` — what `sync-permissions --check` and `sync-resources --check`
 * would say, asked of the deployed revision itself: its own WorkOS key against its own app
 * database, so the two halves can never be pointed at different environments. Read-only. `clean`
 * is the verdict; `problems` is one sentence per finding, for the deploy log.
 */
export const provisioningResponseSchema = z.object({
  clean: z.boolean(),
  problems: z.array(z.string()),
  permissions: z.object({
    missingPermissions: z.number(),
    missingRoles: z.number(),
    missingAttachments: z.number(),
    wrongScope: z.number(),
    /** In WorkOS, not in the manifest. Informational — never a problem. */
    unmanaged: z.number(),
  }),
  resources: z.object({
    accounts: z.number(),
    missing: z.number(),
    unmapped: z.number(),
    orphaned: z.number(),
    subaccounts: z.number(),
    subaccountsMissing: z.number(),
    subaccountsBlocked: z.number(),
  }),
})

export type ProvisioningResponse = z.infer<typeof provisioningResponseSchema>
